import React from "react";
import dayjs from "dayjs";
import { Box, Text, SimpleGrid, HStack, Icon, useBreakpointValue } from "@chakra-ui/react";
import { MdAccountBalance, MdPerson, MdEmail, MdPublic, MdLocationOn, MdAttachMoney, MdKey, MdCalendarToday } from "react-icons/md";
import InfoRow from "./shared/InfoRow";
import BankStatusBanner from "./shared/BankStatusBanner";
import SensitiveIbanRow from "./shared/SensitiveIbanRow";

/**
 * @param {Object} props
 * @param {Object} props.bank - Bank/Stripe account data
 * @returns {React.ReactElement}
 */
function ArtistBank({ bank }) {
    const isMobile = useBreakpointValue({ base: true, sm: false });

    if (!bank || Object.keys(bank).length === 0) {
        return renderEmptyState(isMobile);
    }

    return (
        <Box>
            <BankHeader isMobile={isMobile} />
            <BankStatusBanner status={bank?.status} adminMessage={bank?.admin_message} isMobile={isMobile} />
            <BankDetails bank={bank} isMobile={isMobile} />
        </Box>
    );
}

/**
 * @param {boolean} isMobile - Mobile viewport flag
 * @returns {React.ReactElement} Empty state when no bank data exists
 */
function renderEmptyState(isMobile) {
    return (
        <Box
            textAlign="center"
            padding={isMobile ? 8 : 12}
            background="rgba(255, 255, 255, 0.02)"
            borderRadius="md"
            border="1px solid rgba(255, 255, 255, 0.05)"
        >
            <Icon as={MdAccountBalance} boxSize={isMobile ? "48px" : "64px"} color="rgba(255, 255, 255, 0.2)" mb={4} />
            <Text color="rgba(255, 255, 255, 0.4)" fontSize={isMobile ? "0.75rem" : "0.8rem"}>
                Aucune information bancaire
            </Text>
        </Box>
    );
}

/**
 * Returns Chakra style props for the bank header container.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function bankHeaderContainerSx(isMobile) {
    return {
        mb: 4,
        padding: isMobile ? "10px 12px" : "12px 14px",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.12)"
    };
}

/**
 * Renders the bank icon badge inside a styled box.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function BankIconBadge({ isMobile }) {
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={isMobile ? "28px" : "32px"}
            height={isMobile ? "28px" : "32px"}
            borderRadius="8px"
            background="rgba(23, 220, 184, 0.15)"
            backdropFilter="blur(10px)"
            border="1px solid rgba(23, 220, 184, 0.3)"
            boxShadow="0 2px 8px rgba(23, 220, 184, 0.2)"
        >
            <Icon as={MdAccountBalance} color="#17dcb8" boxSize={isMobile ? "16px" : "18px"} />
        </Box>
    );
}

/**
 * @param {Object} props
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function BankHeader({ isMobile }) {
    return (
        <Box {...bankHeaderContainerSx(isMobile)}>
            <HStack spacing={2} alignItems="center" mb={1}>
                <BankIconBadge isMobile={isMobile} />
                <Text color="rgba(255, 255, 255, 0.95)" fontSize={isMobile ? "0.85rem" : "0.9rem"}
                    fontWeight={600} letterSpacing="0.3px">
                    Informations bancaires
                </Text>
            </HStack>
            <Text color="rgba(255, 255, 255, 0.6)" fontSize={isMobile ? "0.65rem" : "0.7rem"}
                pl={isMobile ? "36px" : "40px"}>
                Détails du compte bancaire
            </Text>
        </Box>
    );
}

/**
 * Returns Chakra style props for a section label in the bank details.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props for section labels
 */
const SECTION_LABEL_SX = (isMobile) => ({
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: isMobile ? "0.7rem" : "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    mb: 2
});

/**
 * Renders the Stripe ID section of the bank details.
 * @param {Object} props
 * @param {Object} props.bank - Bank data with stripe_account_id
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function BankStripeSection({ bank, isMobile }) {
    return (
        <Box gridColumn="1 / -1">
            <Text {...SECTION_LABEL_SX(isMobile)}>Stripe</Text>
            <InfoRow icon={MdKey} label="ID" value={bank?.stripe_account_id} variant="monospace" isMobile={isMobile} sx={{ mb: 2 }} />
        </Box>
    );
}

/**
 * Renders the account holder info section of the bank details.
 * @param {Object} props
 * @param {Object} props.bank - Bank data with name, lastname, email
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function BankHolderSection({ bank, isMobile }) {
    return (
        <Box>
            <Text {...SECTION_LABEL_SX(isMobile)}>Titulaire</Text>
            <InfoRow icon={MdPerson} label="Prénom" value={bank?.name} isMobile={isMobile} sx={{ mb: 2 }} />
            <InfoRow icon={MdPerson} label="Nom" value={bank?.lastname} isMobile={isMobile} sx={{ mb: 2 }} />
            <InfoRow icon={MdEmail} label="Email" value={bank?.email} isMobile={isMobile} sx={{ mb: 2 }} />
        </Box>
    );
}

/**
 * Renders the bank coordinates section (country, currency, IBAN).
 * @param {Object} props
 * @param {Object} props.bank - Bank data with country, currency, iban
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function BankCoordinatesSection({ bank, isMobile }) {
    return (
        <Box>
            <Text {...SECTION_LABEL_SX(isMobile)}>Coordonnées</Text>
            <InfoRow icon={MdPublic} label="Pays" value={bank?.country} isMobile={isMobile} sx={{ mb: 2 }} />
            <InfoRow icon={MdPublic} label="Code pays" value={bank?.country_code} variant="monospace" isMobile={isMobile} sx={{ mb: 2 }} />
            <InfoRow icon={MdLocationOn} label="Code postal" value={bank?.postal_code} isMobile={isMobile} sx={{ mb: 2 }} />
            <InfoRow icon={MdAttachMoney} label="Devise" value={bank?.currency} variant="chip" isMobile={isMobile} sx={{ mb: 2 }} />
            <SensitiveIbanRow bankId={bank?.id} iban={bank?.iban} isMobile={isMobile} />
        </Box>
    );
}

/**
 * Renders the bank history section with creation and modification dates.
 * @param {Object} props
 * @param {Object} props.bank - Bank data with created_at and modified_at
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function BankHistorySection({ bank, isMobile }) {
    return (
        <Box gridColumn="1 / -1">
            <Text {...SECTION_LABEL_SX(isMobile)}>Historique</Text>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                <Box>
                    <InfoRow icon={MdCalendarToday} label="Créé" value={dayjs(bank?.created_at).format("DD/MM/YYYY")} isMobile={isMobile} />
                </Box>
                <Box>
                    <InfoRow icon={MdCalendarToday} label="Modifié" value={dayjs(bank?.modified_at).format("DD/MM/YYYY")} isMobile={isMobile} />
                </Box>
            </SimpleGrid>
        </Box>
    );
}

/**
 * @param {Object} props
 * @param {Object} props.bank - Bank data
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function BankDetails({ bank, isMobile }) {
    return (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <BankStripeSection bank={bank} isMobile={isMobile} />
            <BankHolderSection bank={bank} isMobile={isMobile} />
            <BankCoordinatesSection bank={bank} isMobile={isMobile} />
            <BankHistorySection bank={bank} isMobile={isMobile} />
        </SimpleGrid>
    );
}

export default ArtistBank;
